/**
 * Ordered column editor shared by the workspace + board settings screens.
 *
 * FRIDAY-43. The editor is fully controlled: the screen owns the pending
 * order and reports every change through `onChange`, so the screen keeps the
 * source of truth (and the PUT payload) in one place.
 *
 * `title` is the Name field: the server always normalizes it to the front, so
 * the UI must not offer a way to hide or move it. Every other field can be
 * included/excluded and reordered with explicit buttons — no drag-and-drop, so
 * the whole thing stays keyboard accessible.
 */

import { useEffect, useRef, useState } from 'react'
import { LIST_FIELDS, normalizeListFields, type ListField } from '@friday/shared/listFields'
import { ConfirmDialog } from '@/components/feedback'
import { useUnsavedChangesGuard } from '@/features/settings/useUnsavedChangesGuard'
import type { ListDefaultsConflict } from '@/features/settings/useListDefaultsDraft'
import { cn } from '@/lib/utils'

const FIELD_LABELS = new Map<string, string>(
  LIST_FIELDS.map(([id, label]) => [id, label] as [string, string]),
)

export interface ListFieldsEditorProps {
  /** The tier's OWN order. null = the tier has no row and inherits. */
  value: ListField[] | null
  /** The pending edit. Falls back to the loaded value when there is none. */
  pending?: ListField[] | null
  /** What the tier falls back to when it has no row of its own. */
  inheritedValue: ListField[]
  /** Where `inheritedValue` came from. */
  source: 'workspace' | 'application'
  /** false renders the editor read-only (no Save button at all). */
  canEdit: boolean
  /** Shown when `canEdit` is false. */
  readOnlyReason?: string
  saving: boolean
  /** Error text (409 conflicts included). */
  error: string | null
  dirty: boolean
  onChange: (next: ListField[]) => void
  onSave: () => void
  onCancel: () => void
  /** Sends `fields: null` — resets the tier to inherit. */
  onResetToInherit: () => void
  /** A 409 on this draft: the draft is kept; the user discards or rebases it. */
  conflict?: ListDefaultsConflict | null
  /** Non-blocking note, e.g. the server row moved on while editing. */
  notice?: string | null
  /** Heading, e.g. "Workspace default columns". */
  label: string
  /** Which tier this editor controls — drives the wording of the source note. */
  subject?: 'workspace' | 'board'
}

export function ListFieldsEditor({
  value,
  pending,
  inheritedValue,
  source,
  canEdit,
  readOnlyReason,
  saving,
  error,
  dirty,
  onChange,
  onSave,
  onCancel,
  onResetToInherit,
  label,
  subject = 'board',
  conflict = null,
  notice = null,
}: ListFieldsEditorProps) {
  const [confirming, setConfirming] = useState(false)
  // Focus is returned here after the reset dialog closes, so a keyboard user
  // is not dropped at the top of the document.
  const resetButtonRef = useRef<HTMLButtonElement>(null)

  // A pending edit must survive an accidental reload/tab close.
  useUnsavedChangesGuard(dirty && !saving)

  // The order that is rendered: the pending edit when there is one, otherwise
  // the tier's own row, otherwise what the tier inherits.
  const order = normalizeListFields(pending ?? value ?? inheritedValue)
  const includedSet = new Set(order)
  // Included fields first (in the pending order), then the ones that are
  // currently hidden — every registry field stays reachable.
  const rows = [...order, ...LIST_FIELDS.map(([id]) => id).filter((id) => !includedSet.has(id))]

  // The reset confirmation stays open while the write is in flight and closes
  // once it settles (success or failure — the error is shown inline).
  const wasSaving = useRef(false)
  useEffect(() => {
    if (wasSaving.current && !saving) setConfirming(false)
    wasSaving.current = saving
  }, [saving])

  // The dialog unmounts on close; give the focus back to the button that
  // opened it so keyboard users keep their place. Guarded on the transition
  // so the first render does not steal focus into the page.
  const wasConfirming = useRef(false)
  useEffect(() => {
    if (wasConfirming.current && !confirming) resetButtonRef.current?.focus()
    wasConfirming.current = confirming
  }, [confirming])

  function commit(next: ListField[]) {
    // Normalizing here is what guarantees `title` stays first and no unknown
    // id ever reaches the API.
    onChange(normalizeListFields(next))
  }

  function toggle(id: ListField) {
    if (id === 'title') return
    commit(includedSet.has(id) ? order.filter((field) => field !== id) : [...order, id])
  }

  function move(id: ListField, offset: -1 | 1) {
    const index = order.indexOf(id)
    const target = index + offset
    if (index < 0 || target < 0 || target >= order.length) return
    const next = [...order]
    next.splice(target, 0, ...next.splice(index, 1))
    commit(next)
  }

  return (
    <section className="card p-4" aria-label={label}>
      <h2 className="text-sm font-semibold">{label}</h2>
      <p className="text-surface-500 mt-1 text-xs">{sourceNote(value, source, subject)}</p>

      {!canEdit && readOnlyReason && <p className="text-surface-500 mt-2 text-xs">{readOnlyReason}</p>}

      <ul className="divide-surface-200 dark:divide-surface-700 mt-3 divide-y">
        {rows.map((id) => {
          const fieldLabel = FIELD_LABELS.get(id) ?? id
          const isTitle = id === 'title'
          const on = includedSet.has(id)
          const index = order.indexOf(id)
          // While a save is in flight the editor locks every mutating
          // control so a second edit cannot land on `pending` and be silently
          // dropped when the in-flight PUT clears the pending state on its
          // way back. The save + cancel buttons already disable themselves.
          const locked = saving
          return (
            <li key={id} className="flex items-center gap-2 py-1">
              <label className="flex min-w-0 flex-1 items-center gap-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 shrink-0"
                  checked={on}
                  disabled={isTitle || !canEdit || locked}
                  onChange={() => toggle(id)}
                />
                <span className={cn('truncate text-sm', !on && 'text-surface-500')}>{fieldLabel}</span>
              </label>
              {!isTitle && (
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    className="btn-ghost min-h-11 w-11 px-0 text-sm"
                    aria-label={`Move ${fieldLabel} up`}
                    title={on ? undefined : 'Show this column before it can be moved'}
                    disabled={!canEdit || locked || !on || index <= 0}
                    onClick={() => move(id, -1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="btn-ghost min-h-11 w-11 px-0 text-sm"
                    aria-label={`Move ${fieldLabel} down`}
                    title={on ? undefined : 'Show this column before it can be moved'}
                    disabled={!canEdit || locked || !on || index < 0 || index >= order.length - 1}
                    onClick={() => move(id, 1)}
                  >
                    ↓
                  </button>
                </div>
              )}
            </li>
          )
        })}
      </ul>

      <p aria-live="polite" className="text-surface-500 mt-2 text-xs">
        {dirty ? 'Unsaved changes' : 'No pending changes'}
      </p>

      {error && (
        <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-300">
          {error}
        </p>
      )}

      {notice && !conflict && <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">{notice}</p>}

      {conflict && (
        <div role="alert" className="mt-2 rounded-md border border-red-300 p-3 text-sm dark:border-red-800">
          <p className="text-red-600 dark:text-red-300">{conflict.message}</p>
          <p className="text-surface-600 dark:text-surface-300 mt-1 break-words">{conflict.latest}</p>
          {canEdit && (
            <div className="mt-2 flex flex-wrap gap-2">
              <button type="button" className="btn-ghost min-h-11" disabled={saving} onClick={conflict.onDiscard}>
                Discard my changes
              </button>
              <button
                type="button"
                className="btn-ghost min-h-11"
                disabled={saving || !conflict.canKeep}
                onClick={conflict.onKeep}
              >
                Keep my changes
              </button>
            </div>
          )}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {canEdit && (
          <button type="button" className="btn-primary min-h-11" disabled={!dirty || saving} onClick={onSave}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}
        <button type="button" className="btn-ghost min-h-11" disabled={saving || !dirty} onClick={onCancel}>
          Cancel
        </button>
        {canEdit && value !== null && (
          <button
            type="button"
            ref={resetButtonRef}
            className="btn-ghost min-h-11"
            disabled={saving}
            onClick={() => setConfirming(true)}
          >
            Reset to inherit
          </button>
        )}
      </div>

      <ConfirmDialog
        open={confirming}
        title="Reset to inherit?"
        body={
          <p className="text-surface-600 dark:text-surface-300 text-sm">
            {subject === 'board'
              ? 'This board goes back to the workspace default columns. The board’s own column layout is dropped.'
              : 'This workspace goes back to the application default columns. The workspace’s own column layout is dropped.'}
          </p>
        }
        confirmLabel="Reset to inherit"
        destructive
        busy={saving}
        onClose={() => !saving && setConfirming(false)}
        onConfirm={() => onResetToInherit()}
      />
    </section>
  )
}

/**
 * Explains which tier the columns currently resolve to. The wording follows
 * the tier being edited: a board inherits the workspace default, a workspace
 * inherits the application default.
 */
function sourceNote(
  value: ListField[] | null,
  source: 'workspace' | 'application',
  subject: 'workspace' | 'board',
): string {
  if (subject === 'workspace') {
    return value === null
      ? 'Columns come from the application default.'
      : 'This workspace overrides the application default.'
  }
  if (value !== null) return 'This board overrides the workspace default.'
  return source === 'workspace'
    ? "Columns come from this workspace's default."
    : 'This workspace has no default columns yet, so columns come from the application default.'
}
